import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, ErrorMessage, Icon, Loading, Modal, TextArea, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { driverProfileApi } from "@/redux/apis";
import { useUpdateFleetStatusMutation } from "@/redux/apis/driver/driverApi";
import { useAppDispatch } from "@/redux/hooks";
import { addToast } from "@/utils";
import { useProfileContext } from "../context";
import { DeclineDriverFormData, declineDriverFormSchema } from "../form";

interface DeclineDriverModalProps {
  open: boolean;
  onClose: () => void;
}

export const DeclineDriverModal = ({ open, onClose }: DeclineDriverModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { driver } = useProfileContext();
  const [updateFleetStatus, { isLoading }] = useUpdateFleetStatusMutation();

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<DeclineDriverFormData>({
    resolver: zodResolver(declineDriverFormSchema),
  });

  const handleOnClose = () => {
    if (!isLoading) onClose();
  };

  const onSubmit = handleSubmit((data) => {
    updateFleetStatus({
      id: driver.uuid,
      account_status: "Declined",
      account_status_reason: data.reason,
    })
      .unwrap()
      .then(() => {
        dispatch(driverProfileApi.util.invalidateTags(["DriverProfile"]));
        addToast("success", "Successfully declined driver.");
        onClose();
        navigate("../../");
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  });

  return (
    <Modal open={open} onClose={handleOnClose} className="relative w-[80vw] md:w-[400px] lg:w-[400px]">
      {isLoading && <Loading />}
      <div className="flex flex-col gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
          <Icon name="Danger" variant="Bold" size="lg" className="text-danger" />
        </div>
      </div>
      <form onSubmit={onSubmit} className="mt-4">
        <Typography variant="title">Reason for Declining Driver</Typography>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          Declining this driver&apos;s application will prevent them from joining your team. Please let the driver know the reason below.
        </Typography>
        <div className="mt-5">
          <TextArea hideCharCount placeholder="Enter your reason" hasError={!!formErrors.reason} maxLength={2048} {...register("reason")} />
          <ErrorMessage errors={formErrors} name="reason" />
          <Typography variant="paragraph" className="mt-1 text-neutral-silver-gray">
            The reason you provide will be shown to the driver
          </Typography>
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <Button type="submit" size="lg" variant="primary" className="w-full border-danger bg-danger hover:bg-danger-dark" disabled={isLoading}>
            Decline Driver
          </Button>
          <Button size="lg" variant="secondary" className="w-full" onClick={handleOnClose} disabled={isLoading}>
            Back
          </Button>
        </div>
      </form>
    </Modal>
  );
};
